import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck } from "@fortawesome/pro-solid-svg-icons";
import "./style.css";

export interface JsonCodeBlockProps {
  value: unknown;
}

const isEmpty = (v: unknown): boolean =>
  v == null || (typeof v === "object" && Object.keys(v as object).length === 0);

// Wrap JSON tokens in colored spans. Operates on already-escaped JSON text.
const highlight = (json: string): string =>
  json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = "jcb-number";
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? "jcb-key" : "jcb-string";
        } else if (/true|false/.test(match)) {
          cls = "jcb-boolean";
        } else if (/null/.test(match)) {
          cls = "jcb-null";
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );

export const JsonCodeBlock = ({ value }: JsonCodeBlockProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const raw = useMemo(
    () => (isEmpty(value) ? "" : JSON.stringify(value, null, 2)),
    [value]
  );
  const html = useMemo(() => highlight(raw), [raw]);

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(raw)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  if (!raw) {
    return <div className="jcb-empty">No content</div>;
  }

  return (
    <div className="jcb-wrap">
      <button type="button" className="jcb-copy" onClick={handleCopy}>
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />{" "}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre
        className="jcb-pre"
        data-testid="json-code-block"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};
